import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useGameData } from "./GameContext";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { gameDetails } from "../App";
import { useLanguage } from "../context/LanguageContext";
import Navbar from "../components/Navbar";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

interface HoroscopeData {
    planetId: number;
    planetName: string;
    angle: number;
    horoscopeId: number;
    horoscopeName: string;
}

interface HoroscopeInfo {
    latitude: string;
    longitude: string;
    date: string;
    time: string;
    country: string;
    state: string;
}

interface HoroscopeListItem {
    Header: string;
    SubHeader1: string;
    SubHeader2: string;
    SubHeader3: string;
    SubHeader1Score: number;
    SubHeader2Score: number;
    SubHeader3Score: number;
    Puan: number;
    subVeri: {
        planet: string;
        horoscope: string;
        highHeader1Score: number;
        highHeader2Score: number;
        highHeader3Score: number;
    };
}

interface GameSession {
    userId: string;
    sessionTime: number;
    maxLevel: number;
    confirmClicked: boolean;
    purchaseClicked: boolean;
    HoroscopeInfo?: string;
    horoscopeData?: string;
    HoroscopeList?: string;
}

const UserCard: React.FC<{ session: GameSession }> = ({ session }) => {
    const { t } = useLanguage();

    const formatSessionTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        let result = "";
        if (hours > 0) result += `${hours}${t("h")} `;
        if (minutes > 0) result += `${minutes}${t("m")} `;
        if (remainingSeconds > 0 || result === "") result += `${remainingSeconds}${t("s")}`;
        return result.trim();
    };

    let horoscopeInfo: HoroscopeInfo = { latitude: "", longitude: "", date: "", time: "", country: "", state: "" };
    let horoscopeData: HoroscopeData[] = [];
    let horoscopeList: HoroscopeListItem[] = [];

    try {
        if (session.HoroscopeInfo) horoscopeInfo = JSON.parse(session.HoroscopeInfo);
    } catch (error) { }
    try {
        if (session.horoscopeData) horoscopeData = JSON.parse(session.horoscopeData);
    } catch (error) { }
    try {
        if (session.HoroscopeList) horoscopeList = JSON.parse(session.HoroscopeList);
    } catch (error) { }

    return (
        <motion.div
            className="bg-white rounded-xl shadow-lg p-5 hover:shadow-xl transition-shadow duration-200"
            variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } }}
            initial="hidden"
            animate="visible"
        >
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {" "}
                <span className="relative group">
                    <span className="text-gray-800">#</span>
                    <span className="absolute left-full ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-100 px-2 py-1 rounded text-sm text-gray-700">
                        {session.userId}
                    </span>
                </span>
            </h3>
            <div className="space-y-2 text-gray-600">
                <p>
                    <span className="font-medium text-blue-600">{t("session_time")}:</span> {formatSessionTime(session.sessionTime)}
                </p>
                <p>
                    <span className="font-medium text-red-600">{t("max_level")}:</span> {session.maxLevel}
                </p>
                <p>
                    <span className="font-medium text-green-600">{t("confirm_clicked")}:</span>{" "}
                    {session.confirmClicked ? t("yes") : t("no")}
                </p>
                <p>
                    <span className="font-medium text-purple-600">{t("purchase_clicked")}:</span>{" "}
                    {session.purchaseClicked ? t("yes") : t("no")}
                </p>
            </div>

            <div className="mt-4">
                <h4 className="text-md font-semibold text-gray-700 mb-2">{t("horoscope_info")}</h4>
                {Object.keys(horoscopeInfo).length > 0 && horoscopeInfo.latitude ? (
                    <div className="space-y-1 text-gray-600">
                        <p>
                            <span className="font-medium">{t("location")}:</span> {horoscopeInfo.latitude}, {horoscopeInfo.longitude}
                        </p>
                        <p>
                            <span className="font-medium">{t("date")}:</span> {horoscopeInfo.date} {horoscopeInfo.time}
                        </p>
                        <p>
                            <span className="font-medium">{t("country_state")}:</span> {horoscopeInfo.country}, {horoscopeInfo.state}
                        </p>
                    </div>
                ) : (
                    <p className="text-gray-500">{t("no_horoscope_info")}</p>
                )}
            </div>

            <div className="mt-4">
                <h4 className="text-md font-semibold text-gray-700 mb-2">{t("horoscope_data")}</h4>
                {horoscopeData.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-gray-600">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="py-2 px-3 text-left font-medium">{t("planet")}</th>
                                    <th className="py-2 px-3 text-left font-medium">{t("angle")}</th>
                                    <th className="py-2 px-3 text-left font-medium">{t("horoscope")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {horoscopeData.map((data) => (
                                    <tr key={data.planetId} className="border-b">
                                        <td className="py-2 px-3">{data.planetName}</td>
                                        <td className="py-2 px-3">{data.angle.toFixed(2)}°</td>
                                        <td className="py-2 px-3">{data.horoscopeName}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-gray-500">{t("no_horoscope_data")}</p>
                )}
            </div>

            {session.HoroscopeList && (
                <Link
                    to={`/users/game4/${session.userId}`}
                    className="mt-4 block w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-2 rounded-lg text-center font-medium hover:from-blue-600 hover:to-indigo-600 transition-all duration-200"
                >
                    {t("view_details")}
                </Link>
            )}
        </motion.div>
    );
};

const UsersPageAstroad: React.FC = () => {
    const navigate = useNavigate();
    const { gameData } = useGameData();
    const { language, setLanguage, t } = useLanguage();
    const sessions = gameData["game4"] || [];

    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [country, setCountry] = useState<string>("");
    const [state, setState] = useState<string>("");
    const [hasHoroscopeList, setHasHoroscopeList] = useState<boolean>(false);
    const [hasHoroscopeData, setHasHoroscopeData] = useState<boolean>(false);
    const [hasHoroscopeInfo, setHasHoroscopeInfo] = useState<boolean>(false);

    const handleBack = () => navigate("/");

    const filteredSessions = sessions.filter((session) => {
        let horoscopeInfo: HoroscopeInfo = { latitude: "", longitude: "", date: "", time: "", country: "", state: "" };
        try {
            if (session.HoroscopeInfo) horoscopeInfo = JSON.parse(session.HoroscopeInfo);
        } catch (error) { }

        const sessionDate = horoscopeInfo.date ? new Date(horoscopeInfo.date) : null;
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        return (
            (!start || (sessionDate && sessionDate >= start)) &&
            (!end || (sessionDate && sessionDate <= end)) &&
            (!country || horoscopeInfo.country.toLowerCase().includes(country.toLowerCase())) &&
            (!state || horoscopeInfo.state.toLowerCase().includes(state.toLowerCase())) &&
            (!hasHoroscopeList || session.HoroscopeList) &&
            (!hasHoroscopeData || session.horoscopeData) &&
            (!hasHoroscopeInfo || session.HoroscopeInfo)
        );
    });

    const purchaseData = {
        labels: [t("purchased"), t("not_purchased")],
        datasets: [
            {
                label: t("purchase"),
                data: [
                    filteredSessions.filter((s) => s.purchaseClicked).length,
                    filteredSessions.filter((s) => !s.purchaseClicked).length,
                ],
                backgroundColor: ["rgba(0, 172, 193, 0.7)", "rgba(255, 87, 34, 0.7)"],
                borderColor: ["rgba(0, 172, 193, 1)", "rgba(255, 87, 34, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const confirmData = {
        labels: [t("confirmed"), t("not_confirmed")],
        datasets: [
            {
                label: t("confirm"),
                data: [
                    filteredSessions.filter((s) => s.confirmClicked).length,
                    filteredSessions.filter((s) => !s.confirmClicked).length,
                ],
                backgroundColor: ["rgba(0, 172, 193, 0.7)", "rgba(255, 87, 34, 0.7)"],
                borderColor: ["rgba(0, 172, 193, 1)", "rgba(255, 87, 34, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const generalStatsData = {
        labels: [t("avg_session_s"), t("avg_max_level")],
        datasets: [
            {
                label: t("general_stats"),
                data: [
                    filteredSessions.length > 0 ? filteredSessions.reduce((sum, s) => sum + s.sessionTime, 0) / filteredSessions.length : 0,
                    filteredSessions.length > 0 ? filteredSessions.reduce((sum, s) => sum + s.maxLevel, 0) / filteredSessions.length : 0,
                ],
                backgroundColor: ["rgba(0, 172, 193, 0.7)", "rgba(255, 152, 0, 0.7)"],
                borderColor: ["rgba(0, 172, 193, 1)", "rgba(255, 152, 0, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "top" as const, labels: { font: { size: 12 } } },
            title: {
                display: true,
                font: { size: 16, weight: "600" as const },
                color: "#1f2937",
            },
            datalabels: {
                display: true,
                color: "#fff",
                font: { weight: "bold" as const, size: 12 },
                formatter: (value: number) => value.toFixed(2),
                anchor: "center" as const,
                align: "center" as const,
            },
        },
        scales: {
            y: { beginAtZero: true, grid: { color: "rgba(0, 0, 0, 0.05)" } },
            x: { grid: { display: false } },
        },
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Navbar /> {/* Use the Navbar component here */}

            {/* İçerik */}
            <main className="mt-36 mb-16 flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8 flex items-center justify-between"
                >
                    <div className="flex items-center space-x-4">
                        <img src={gameDetails["game4"]?.logo || ""} alt="Astroad" className="w-12 h-12 rounded-full" />
                        <h2 className="text-3xl font-bold text-gray-800">Astroad {t("users")}</h2>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleBack}
                        className="bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
                    >
                        {t("back_to_dashboard")}
                    </motion.button>
                </motion.div>

                {/* Grafik Bölümü */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={purchaseData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("purchase_stats"), font: { size: 16, weight: 600 }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={confirmData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("confirm_stats"), font: { size: 16, weight: 600 }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={generalStatsData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("general_stats"), font: { size: 16, weight: 600 }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                </div>

                {/* Filtreleme Bölümü */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">{t("filters")}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("date_range")}</label>
                            <div className="flex items-center space-x-2">
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <span className="text-gray-500">-</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("country")}</label>
                            <input
                                type="text"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                placeholder={t("eg_country")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("state")}</label>
                            <input
                                type="text"
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                                placeholder={t("eg_state")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("data_filters")}</label>
                            <div className="space-y-2">
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={hasHoroscopeList}
                                        onChange={(e) => setHasHoroscopeList(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-600">{t("has_horoscope_list")}</span>
                                </label>
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={hasHoroscopeData}
                                        onChange={(e) => setHasHoroscopeData(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-600">{t("has_horoscope_data")}</span>
                                </label>
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={hasHoroscopeInfo}
                                        onChange={(e) => setHasHoroscopeInfo(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-600">{t("has_horoscope_info")}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Kullanıcı Kartları */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredSessions.length > 0 ? (
                        filteredSessions.map((session) => <UserCard key={session.userId} session={session} />)
                    ) : (
                        <p className="col-span-full text-gray-500 text-center py-8">{t("no_users_found")}</p>
                    )}
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gray-800 py-4">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <p className="text-gray-300 text-sm">{t("footer", { year: new Date().getFullYear() })}</p>
                </div>
            </footer>
        </div>
    );
};

export default UsersPageAstroad;